import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { translate } from '../../../utils/Common';
import styles from './SearchInputWithField.module.css';
import Select from '../Select';
import * as FaIcons from 'react-icons/fa';

const SearchInputWithField = ({
  value,
  onChange,
  placeholder = 'Search...',
  fieldOptions = [],
  selectedField = 'name',
  onFieldChange,
  onKeyPress,
  disabled = false,
  loading = false,
  className = '',
  // Nuevas props para chips
  activeFilters = {}, // Objeto con los filtros activos: { name: 'valor1', apellido: 'valor2' }
  onChipRemove, // Función para eliminar un chip: (field) => {}
  onAddFilter, // Función para agregar un nuevo filtro: (field, value) => {}
  ...rest
}) => {
  const [isFieldSelectorOpen, setIsFieldSelectorOpen] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Cerrar selector al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsFieldSelectorOpen(false);
      }
    };

    if (isFieldSelectorOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isFieldSelectorOpen]);

  const handleFieldChange = (field) => {
    if (onFieldChange) {
      onFieldChange(field);
    }
    setIsFieldSelectorOpen(false);
  };

  // Manejar cuando se presiona Enter o se busca
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && value && value.trim()) {
      // Si el campo seleccionado ya tiene un valor, reemplazarlo
      // Si no, agregar un nuevo chip
      if (onAddFilter) {
        onAddFilter(selectedField, value.trim());
      }
      // Limpiar el input después de agregar
      if (onChange) {
        onChange({ target: { value: '' } });
      }
    }
    if (onKeyPress) {
      onKeyPress(e);
    }
  };

  // Eliminar un chip
  const handleChipRemove = (field) => {
    if (onChipRemove) {
      onChipRemove(field);
    }
  };

  const selectedFieldOption = fieldOptions.find(opt => opt.value === selectedField) || fieldOptions[0];

  // Obtener el label del campo
  const getFieldLabel = (fieldValue) => {
    const option = fieldOptions.find(opt => opt.value === fieldValue);
    return option ? option.label : fieldValue;
  };

  const containerClasses = [
    styles.container,
    disabled && styles.disabled,
    className
  ].filter(Boolean).join(' ');

  // Obtener chips activos (excluir status y clientType que son filtros especiales)
  const activeChips = Object.entries(activeFilters || {})
    .filter(([field, filterValue]) => {
      // Solo mostrar chips para campos de búsqueda, no para filtros especiales
      return field !== 'status' && field !== 'clientType' && filterValue && filterValue.trim();
    });

  return (
    <div className={containerClasses} ref={containerRef}>
      <div className={styles.searchInputWrapper}>
        {/* Icono de búsqueda */}
        <span className={styles.searchIcon}>
          <FaIcons.FaSearch size={16} />
        </span>

        {/* Selector de campo */}
        {fieldOptions.length > 0 && (
          <div className={styles.fieldSelectorContainer}>
            <div className={styles.selectWrapper}>
              <Select
                value={selectedField}
                onChange={(e) => handleFieldChange(e.target.value)}
                options={fieldOptions}
                size="sm"
              />
            </div>
            <span className={styles.divider}>|</span>
          </div>
        )}

        {/* Input de búsqueda */}
        <input
          ref={inputRef}
          type="text"
          className={styles.searchInput}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onKeyPress={handleKeyPress}
          disabled={disabled || loading}
          {...rest}
        />
      </div>

      {/* Chips de filtros activos - ahora debajo del input */}
      {activeChips.length > 0 && (
        <div className={styles.chipsContainer}>
          {activeChips.map(([field, filterValue]) => (
            <div key={field} className={styles.chip}>
              <span className={styles.chipLabel}>{getFieldLabel(field)}:</span>
              <span className={styles.chipValue}>{filterValue}</span>
              <button
                type="button"
                className={styles.chipRemove}
                onClick={() => handleChipRemove(field)}
                disabled={disabled || loading}
                aria-label={`Eliminar filtro ${getFieldLabel(field)}`}
              >
                <FaIcons.FaTimes size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

SearchInputWithField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  fieldOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  selectedField: PropTypes.string,
  onFieldChange: PropTypes.func.isRequired,
  onKeyPress: PropTypes.func,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  className: PropTypes.string,
  // Nuevas props
  activeFilters: PropTypes.object,
  onChipRemove: PropTypes.func,
  onAddFilter: PropTypes.func,
};

export default SearchInputWithField;

